import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { imageUrl } from '../../../../common/constants/ProductOfferingConstants.js';
import { dsClass } from '../../../../common/constants/dsClasses';
import {
  addMsg,
  quantityMsg,
  removeMsg,
  subtractMsg,
  t,
  totalWithCurlyBracketsMsg,
  vatPercentageMsg,
} from '../../../../common/i18n';
import { eurosToCents, formatSum } from '../../../../common/utils/priceUtils.js';
import { getLocalizedString, getMaxProductSelection } from '../../../../common/utils/productOfferingsUtils.js';
import { PriceType } from '../../../HorizontalSubscriptionCard/types.js';
import { ProductAvailabilityCoCo } from '../../../ProductAvailabilityCoCo/ProductAvailabilityCoCo.js';
import { DisclaimerFields } from '../../../ShoppingBasket/DisclaimerFields.js';
import {
  getEffectivePrices,
  getPrice,
  getProductPriceToDisplay,
} from '../../../ShoppingBasket/shoppingBasketHelpersCoCo.js';
import { getOnetimePaymentText } from '../../../ShoppingBasket/shoppingBasketUtils.js';
import type { ProductOfferingCartItems } from '../../../../common/types/shoppingBasketCoCo.js';
import type { ProductOfferingPrice } from '../../../../generated/api/productOfferingPrice.js';
import type { ProductAvailability } from '../../../DeviceDetailsCoCo/deviceDetailsUtilsCoCo.js';
import type { PriceData } from '../../../ShoppingBasket/types.js';
import type { JSX } from 'react';

interface ProductEntryViewProps {
  productName: string;
  productImage: JSX.Element;
  description?: JSX.Element;
  availability?: JSX.Element;
  price: JSX.Element;
  disclaimerFields: JSX.Element;
  quantityField?: JSX.Element;
  readOnly?: boolean;
}

export const ProductEntryView = (props: ProductEntryViewProps) => {
  return (
    <div className={classNames('of-place-order-product-entry', dsClass.SHOPPINGCART_PRODUCT_INFO)}>
      <div aria-hidden="true" className={dsClass.SHOPPINGCART_PRODUCT_IMAGE}>
        {props.productImage}
      </div>
      <div className={dsClass.SHOPPINGCART_PRODUCT_DETAILS}>
        <div className={dsClass.SHOPPINGCART_PRODUCT_NAME_AND_DESCRIPTION}>
          <h3 className={dsClass.SHOPPINGCART_PRODUCT_NAME}>{props.productName}</h3>
          <div className={dsClass.SHOPPINGCART_PRODUCT_DESCRIPTION}>{props.description}</div>
          {props.availability}
        </div>
        <div className={dsClass.SHOPPINGCART_PRODUCT_PRICE_AND_DISCLAIMER}>
          {!props.readOnly && props.quantityField && (
            <div className={classNames(dsClass.SHOPPINGCART_PRODUCT_QUANTITY, dsClass.MARGIN_BOTTOM_2)}>
              {props.quantityField}
            </div>
          )}
          <div className={dsClass.SHOPPINGCART_PRODUCT_PRICE}>{props.price}</div>
          <div className={dsClass.SHOPPINGCART_PRODUCT_DISCLAIMER}>{props.disclaimerFields}</div>
        </div>
      </div>
    </div>
  );
};

export interface ProductEntryProps {
  basketItem: ProductOfferingCartItems;
  groupItems?: ProductOfferingCartItems[];
  changeQuantity?: (newQty: number, groupItems: ProductOfferingCartItems[]) => void;
  readOnly?: boolean;
  availabilityClassName?: string;
  /**
   * Availability of the matching product offering. Product agnostic on purpose: the entry only
   * renders what it is given, so offerings without stock data simply show nothing.
   */
  availability?: ProductAvailability | null;
  imageOverride?: string;
}

const getSelectedPaymentTermMonths = (prices: ProductOfferingPrice[]): number | undefined => {
  const selectedInstallmentTerm = prices.flatMap(price => price.installmentTerm ?? [])[0];
  if (selectedInstallmentTerm?.duration && selectedInstallmentTerm.duration > 1) {
    return selectedInstallmentTerm.duration;
  }

  const selectedInstallmentOption = prices.flatMap(price => price.installmentOption ?? [])[0];
  if (selectedInstallmentOption?.numberOfInstallment && selectedInstallmentOption.numberOfInstallment > 1) {
    return selectedInstallmentOption.numberOfInstallment;
  }

  return undefined;
};

export const ProductEntryCoCo = ({
  basketItem,
  groupItems = [basketItem],
  changeQuantity,
  readOnly,
  availabilityClassName,
  availability,
  imageOverride,
}: ProductEntryProps) => {
  const quantity = groupItems.length;
  const effectivePrices = getEffectivePrices(basketItem);
  const displayPrice = getProductPriceToDisplay(effectivePrices, quantity);
  const priceElement = (
    <>
      <output className={dsClass.SHOPPINGCART_PRICE_AMOUNT}>{displayPrice.amount}</output>
      <span className={dsClass.SHOPPINGCART_PRICE_UNIT}>{displayPrice.unit}</span>
    </>
  );

  const quantityField = (
    <CL.Quantity
      i18n_quantity_deleteAriaLabel={t.R3VE(removeMsg)}
      i18n_quantity_minusAriaLabel={t.C2KQ(subtractMsg)}
      i18n_quantity_plusAriaLabel={t.VKFM(addMsg)}
      i18n_quantity_quantityAriaLabel={t.M0W7(quantityMsg)}
      className={dsClass.SHOPPINGCART_PRODUCT_QUANTITY}
      currentValue={quantity}
      minValue={1}
      maxValue={getMaxProductSelection(basketItem.productOfferingType?.subcategory || '')}
      onChange={(newQty: number) => {
        // Quantity calls onRemove and then onChange(minValue) when the delete
        // button is pressed. Do not restore the removed item with that second
        // callback.
        if (newQty !== quantity) {
          changeQuantity?.(newQty, groupItems);
        }
      }}
      onRemove={() => {
        changeQuantity?.(0, groupItems);
      }}
      removeLessThanMin
    />
  );

  const oneTimePaymentText = getOnetimePaymentText(
    eurosToCents(getPrice(effectivePrices, PriceType.ONE_TIME_CHARGE)),
    quantity,
    true
  );
  const selectedPaymentTermMonths = getSelectedPaymentTermMonths(effectivePrices);
  const selectedRecurringPrice = getPrice(effectivePrices, PriceType.RECURRING_CHARGE);

  const priceData: PriceData = {
    price: {
      monthlyRecurringCharge: getPrice(effectivePrices, PriceType.RECURRING_CHARGE),
      oneTimeCharge: getPrice(effectivePrices, PriceType.ONE_TIME_CHARGE),
    },
  };

  const vatText = t.A0OJ(vatPercentageMsg, '0');
  const totalPaymentTermPrice = selectedPaymentTermMonths
    ? formatSum(eurosToCents(selectedRecurringPrice * selectedPaymentTermMonths * quantity))
    : undefined;
  const paymentTermDisclaimers =
    selectedPaymentTermMonths && totalPaymentTermPrice ? (
      <>
        <div>{t.CF93('{} month agreement', String(selectedPaymentTermMonths))}</div>
        <div>{t.W1RX(totalWithCurlyBracketsMsg, totalPaymentTermPrice)}</div>
        <div>{vatText}</div>
      </>
    ) : null;
  const priceDisclaimers = paymentTermDisclaimers ?? (
    <DisclaimerFields oneTimePaymentText={oneTimePaymentText} priceData={priceData} />
  );

  const quantityText = t.BDBD('{} pcs', String(quantity));

  const description = (() => {
    if (readOnly) {
      return <span>{basketItem.qualificationAddressText ?? quantityText}</span>;
    }
    return <div>{getLocalizedString(basketItem.description ?? '')}</div>;
  })();

  return (
    <ProductEntryView
      productName={getLocalizedString(basketItem.name)}
      productImage={
        <img src={basketItem.imageUrl ?? imageOverride ?? imageUrl} alt={getLocalizedString(basketItem.name)} />
      }
      description={description}
      availability={<ProductAvailabilityCoCo availability={availability} className={availabilityClassName} />}
      price={priceElement}
      quantityField={quantityField}
      disclaimerFields={priceDisclaimers}
      readOnly={readOnly}
    />
  );
};
